import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface Submission {
  id: string;
  childName: string;
  promptText: string;
  text: string | null;
  drawing: string | null;
  createdAt: string;
  reviewedAt: string | null;
  discussedTogether: boolean;
  parentNote: string | null;
}

/**
 * Free writing, waiting for a grown-up.
 *
 * This is the honest half of the promise the child screen makes: the app said
 * a grown-up would read it, so there has to be somewhere that actually happens.
 * The rubric is three taps, not a form - the point is the conversation.
 */
export function AdminWriting() {
  const qc = useQueryClient();
  const [showAll, setShowAll] = useState(false);

  const submissions = useQuery({
    queryKey: ['admin', 'writing', showAll],
    queryFn: () => api<Submission[]>(`/admin/writing?pending=${showAll ? 'false' : 'true'}`),
  });

  return (
    <div className="admin">
      <div className="admin-header">
        <div>
          <h1>Their writing</h1>
          <p className="muted">
            The app never marks these. It says so on the child&rsquo;s screen, so reading them
            together is the whole point.
          </p>
        </div>
        <button type="button" className="btn btn--outline" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show only new' : 'Show everything'}
        </button>
      </div>

      {submissions.isPending && <p className="muted">Loading…</p>}
      {submissions.data?.length === 0 && (
        <section className="card">
          <p className="muted">Nothing waiting. When they write something, it will appear here.</p>
        </section>
      )}

      {submissions.data?.map((item) => (
        <SubmissionCard
          key={item.id}
          item={item}
          onReviewed={() => qc.invalidateQueries({ queryKey: ['admin', 'writing'] })}
        />
      ))}
    </div>
  );
}

function SubmissionCard({ item, onReviewed }: { item: Submission; onReviewed: () => void }) {
  const [ideas, setIdeas] = useState(0);
  const [spelling, setSpelling] = useState(0);
  const [punctuation, setPunctuation] = useState(0);
  const [together, setTogether] = useState(item.discussedTogether);
  const [note, setNote] = useState(item.parentNote ?? '');

  const review = useMutation({
    mutationFn: () =>
      api(`/admin/writing/${item.id}/review`, {
        method: 'POST',
        body: {
          ...(ideas ? { ratingIdeas: ideas } : {}),
          ...(spelling ? { ratingSpelling: spelling } : {}),
          ...(punctuation ? { ratingPunctuation: punctuation } : {}),
          discussedTogether: together,
          ...(note.trim() ? { parentNote: note.trim() } : {}),
        },
      }),
    onSuccess: onReviewed,
  });

  const scale = (value: number, set: (n: number) => void, label: string) => (
    <div className="rubric-row">
      <span className="graph-label">{label}</span>
      {[1, 2, 3].map((n) => (
        <button
          key={n}
          type="button"
          className="chip"
          data-selected={value === n}
          onClick={() => set(n)}
        >
          {n === 1 ? 'Getting there' : n === 2 ? 'Good' : 'Great'}
        </button>
      ))}
    </div>
  );

  return (
    <section className="card">
      <div className="child-head">
        <div>
          <h2>
            {item.childName}{' '}
            {item.reviewedAt ? (
              <span className="pill pill--ok">Read together</span>
            ) : (
              <span className="pill pill--warn">Waiting for you</span>
            )}
          </h2>
          <p className="muted">
            {new Date(item.createdAt).toLocaleString('en-AU')} · &ldquo;{item.promptText}&rdquo;
          </p>
        </div>
      </div>

      {item.text && <p className="child-writing">{item.text}</p>}
      {item.drawing && <img className="child-drawing" src={item.drawing} alt="Their drawing" />}

      {!item.reviewedAt && (
        <div className="child-body">
          {scale(ideas, setIdeas, 'Ideas')}
          {scale(spelling, setSpelling, 'Spelling')}
          {scale(punctuation, setPunctuation, 'Punctuation')}

          <label className="switch-row">
            <input
              type="checkbox"
              checked={together}
              onChange={(e) => setTogether(e.target.checked)}
            />
            <span>We read this together</span>
          </label>

          <label>
            A note for yourself
            <input value={note} onChange={(e) => setNote(e.target.value)} />
          </label>

          <button
            type="button"
            className="btn"
            disabled={review.isPending}
            onClick={() => review.mutate()}
          >
            {review.isPending ? 'Saving…' : 'Done'}
          </button>
        </div>
      )}
    </section>
  );
}
